import { useEventListener } from '@vueuse/core'

import type { UseTopmostEscapeOptions } from '@/composables/use-topmost-escape/useTopmostEscape.types'

/** Every overlay layer the admin stacks: dialogs, confirms included, and drawers. */
const OVERLAY_MASK_SELECTOR = '.p-dialog-mask, .p-drawer-mask'

const zIndexOf = (element: HTMLElement): number => Number(element.style.zIndex) || 0

/**
 * Whether `other` sits above `mask`. PrimeVue gives each mask an inline z-index from one counter as it
 * opens, so the higher one is on top. On a tie — no z-index yet — the one appended to the page later is.
 */
const isAbove = (other: HTMLElement, mask: HTMLElement): boolean => {
  const difference = zIndexOf(other) - zIndexOf(mask)
  if (difference !== 0) return difference > 0

  return Boolean(mask.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING)
}

/** True when no other overlay sits above this mask. */
const isTopmostOverlay = (mask: HTMLElement | null | undefined): boolean => {
  if (!mask) return false

  return !Array.from(document.querySelectorAll<HTMLElement>(OVERLAY_MASK_SELECTOR)).some(
    other => other !== mask && isAbove(other, mask)
  )
}

/**
 * Closes an overlay on Escape only when it is the one on top.
 *
 * ⚠ PrimeVue's Dialog and Drawer each listen for Escape on `document` and close without checking what
 * sits above them, so Escape on a confirm over a slideover closed both. A primitive that uses this turns
 * PrimeVue's own `closeOnEscape` off and lets this decide.
 */
export const useTopmostEscape = ({ isEnabled, getMask, close }: UseTopmostEscapeOptions): void => {
  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (event.code !== 'Escape' || !isEnabled()) return
    if (!isTopmostOverlay(getMask())) return

    close()
  })
}
